import { ComplexQuestionTypeToString } from "@repo/app-types/client/client";
import { generateSurveyTemplateForProject } from "@repo/app-types/constants/projectTypeToDefaultQuestions";
import type { Company } from "@repo/app-types/entities/company";
import type { Project } from "@repo/app-types/entities/project";
import type {
  ComplexQuestion,
  ComplexQuestionInsert,
} from "@repo/app-types/entities/question";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  type DropdownItem,
  DropdownMenu,
} from "@repo/ui/Dropdown/dropdownMenu";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { MenuTrigger } from "react-aria-components";

const MAX_SURVEY_QUESTIONS = 4;

interface AddSurveyQuestionsDropdownProps {
  project: Project;
  company: Company;
  onAddQuestion: (question: ComplexQuestionInsert) => void;
  complexQuestions: (ComplexQuestion | ComplexQuestionInsert)[];
}

export const AddSurveyQuestionsDropdown: FC<
  AddSurveyQuestionsDropdownProps
> = ({ project, company, onAddQuestion, complexQuestions }) => {
  const dropdownItems: DropdownItem[] = [
    {
      label: `${ComplexQuestionTypeToString["rating"]} question`,
      onClick: () => {
        const newQuestion: ComplexQuestionInsert = {
          ...generateSurveyTemplateForProject(project.name, company.name),
          company_id: company.id,
        };

        onAddQuestion(newQuestion);
      },
    },
    {
      label: `${ComplexQuestionTypeToString["multiple_choice"]} question`,
      onClick: () => {
        const newQuestion: ComplexQuestionInsert = {
          type: "multiple_choice",
          company_id: company.id,
          options: [{ name: "" }, { name: "" }, { name: "" }],
          question: "",
        };

        onAddQuestion(newQuestion);
      },
    },
  ];

  const isAtQuestionLimit = complexQuestions.length >= MAX_SURVEY_QUESTIONS;

  const addButton = (
    <Button
      variant={ButtonVariant.Secondary}
      size={Size.MEDIUM}
      isDisabled={isAtQuestionLimit}
    >
      Add
    </Button>
  );

  return (
    <div className="relative ml-3" onClick={(evt) => evt.stopPropagation()}>
      <MenuTrigger>
        {isAtQuestionLimit ? (
          // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">{addButton}</TooltipHoverTarget>
            <SimpleTooltip>
              Up to {MAX_SURVEY_QUESTIONS} survey questions are supported.
            </SimpleTooltip>
          </TooltipTrigger>
        ) : (
          addButton
        )}
        <DropdownMenu items={dropdownItems} />
      </MenuTrigger>
    </div>
  );
};
